// src/contexts/backend/WebSocketContext.tsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  ReactNode,
} from "react";
import { useAuth } from "./AuthContext";
import { API_BASE } from "../../services/api";
import { WS_BASE } from "../../config/environment";

import { TOPIC } from "./ws-constants";
import {
  ConnectionStatus,
  User,
  Address,
  PayoutMethod,
  TradingAccount,
  FundedPayout,
  Invoice,
  WalletSummaryBalance,
  WalletTransaction,
  LevelState,
  LevelEvent,
  LevelRankTier,
  normalizeLevelRankTier,
  normalizeUser,
  normalizeTradingAccounts,
  normalizeFundedPayouts,
  normalizeLevelState,
  mapAchievementListToLite,
  AchievementLite,
  PartnerLink,
  PartnerCode,
  PartnerData,
  Settlement,
  Ticket,
  TradingStreak,
  normalizeSettlements,
  normalizePartnerData,
  normalizeTradingStreak,
  normalizeTicket,
  ActivityFeedItem,
  Notification,
  normalizeNotifications,
  FundedContract,
  normalizeFundedContracts,
  normalizeInvoice,
} from "./ws-types-and-normalizers";

import { makeSubscribeApi } from "./ws-subscribe";
import {
  makeOutboxHandler,
  isOutboxEnvelope,
  normalizeEnvelope,
  MetaRef,
  normalizeTradesArray,
} from "./ws-handlers";

import type { Commission } from "../../hooks/websocket/useCommissions";
import type { PayoutMethodSetting } from "../../hooks/usePayoutMethodSettings";

/**********************
 * Tunables / Constants
 **********************/
const PAGE_SIZE = 500;
const MAX_PAGE_CONCURRENCY = 3;
const DEFAULT_TIMEOUT = 30_000;
const CRITICAL_TIMEOUT = 45_000;
const INITIAL_LOAD_TIMEOUT = 0; // infinite
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAYS = [1_000, 3_000, 5_000];
const RECONNECT = {
  initialDelay: 1_000,
  maxDelay: 30_000,
  factor: 2,
  jitter: 0.2,
} as const;

const HEARTBEAT_INTERVAL = 60_000;
const HEARTBEAT_TIMEOUT = 150_000;
const MAX_MISSED_PONGS = 2;
const WATCHDOG_INTERVAL = 120_000;

const genId = () =>
  crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

let _refreshPromise: Promise<boolean> | null = null;

async function ensureFreshSession(): Promise<boolean> {
  if (_refreshPromise) return _refreshPromise;

  _refreshPromise = (async () => {
    try {
      const statusRes = await fetch(`${API_BASE}/auth/status`, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      if (statusRes.ok) {
        const body = await statusRes.json();
        if (body?.authenticated === true) return true;
      }

      if (statusRes.status === 401) return false;

      const refreshRes = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      return refreshRes.ok;
    } catch {
      return false;
    }
  })().finally(() => {
    _refreshPromise = null;
  });

  return _refreshPromise;
}

type ChunkPathSegment = string | number;

type ChunkMeta = {
  id?: string;
  index?: number | string;
  count?: number | string;
  isLast?: boolean;
  path?: ChunkPathSegment[];
  groupId?: string;
  parentId?: string | null;
  level?: number | string;
  parentParentId?: string | null;
  parentParentIndex?: number | string;
  parentCount?: number | string;
  parentPath?: ChunkPathSegment[];
  parentLevel?: number | string;
  parentIndex?: number | string;
  transport?: string;
  kind?: string;
  rootId?: string;
  groupCount?: number | string;
};

type ChunkGroupAccumulator = {
  id: string;
  parentId: string | null;
  level: number;
  count: number;
  path: ChunkPathSegment[];
  parentIndex: number;
  firstSeq: number;
  parts: Map<number, any>;
  childGroupIds: Set<string>;
  childGroupByIndex: Map<number, string>;
  completed: any | null;
};

type ChunkRequestAccumulator = {
  sequence: number;
  groups: Map<string, ChunkGroupAccumulator>;
};

type PatchV2GroupAccumulator = {
  id: string;
  path: ChunkPathSegment[];
  count: number;
  parts: Map<number, any[]>;
};

type PatchV2RequestAccumulator = {
  rootId: string | null;
  basePayload: any | null;
  expectedGroupCount: number;
  pendingComplete: boolean;
  groups: Map<string, PatchV2GroupAccumulator>;
  appliedGroupIds: Set<string>;
};

/**********************
 * Cookie Helper
 **********************/
const getBrowserIdFromCookie = (): string | null => {
  const m = document.cookie.match(/(?:^|;\s*)__Secure-browser-id=([^;]*)/);
  return m ? decodeURIComponent(m[1]) : null;
};

/**********************
 * Context Interface
 **********************/
export type WebSocketContextType = {
  status: ConnectionStatus;
  sendMessage: (data: any) => void;
  request: <T = any>(
    action: string,
    payload?: any,
    timeoutMs?: number
  ) => Promise<T>;
  publish: (action: string, payload?: any) => void;
  disconnect: (opts?: { suppressReconnect?: boolean }) => void;
  connectNow: () => void;

  // High-level RPCs
  getLevelStatesList: (payload?: {
    user_id?: string;
    scope?: "active" | "all";
  }) => Promise<{ items: LevelState[] }>;
  ensureLevelStates: () => Promise<void>;

  getFileDownloadUrl: (
    fileId: string,
    opts?: {
      prefer_size?: number;
      filename?: string;
      content_type?: string;
      expires_in?: number;
    }
  ) => Promise<{ url: string; expires_in: number; etag?: string }>;

  // Local state mutations
  removePayoutMethod: (methodId: string) => void;
  removeTradingAccount: (accountId: string) => void;
  updateFundedContractStatus: (contractId: string, status: string) => void;

  data: {
    user: User | null;
    tradingAccounts: Record<string, TradingAccount>;
    fundedPayouts: Record<string, FundedPayout>;
    invoices: Record<string, Invoice>;
    walletBalances: WalletSummaryBalance[];
    walletTransactions: WalletTransaction[];
    walletBuckets: any[];
    achievements: Record<string, AchievementLite>;
    trades: Record<string, any>;
    commissions: Record<string, Commission>;
    settlements: Record<string, Settlement>;
    partnerCodes: Record<string, PartnerCode>;
    partnerLinks: Record<string, PartnerLink>;
    partnerData: PartnerData | null;
    tickets: Record<string, Ticket>;
    tradingStreak: TradingStreak | null;
    notifications: Record<string, Notification>;
    fundedContracts: Record<string, FundedContract>;
    activityFeed: ActivityFeedItem[];
    levels: { statesBySeasonId: Record<string, LevelState> };
    levelTiers: LevelRankTier[];
    payoutMethodSettings: PayoutMethodSetting[];
    walletPayouts: any[];
  };

  meta: {
    userLoaded: boolean;
    tradingAccountsLoaded: boolean;
    fundedPayoutsLoaded: boolean;
    invoicesLoaded: boolean;
    fundedContractsLoaded: boolean;
    walletBalancesLoaded: boolean;
    walletTransactionsLoaded: boolean;
    walletBucketsLoaded: boolean;
    achievementsLoaded: boolean;
    tradesLoaded: boolean;
    commissionsLoaded: boolean;
    settlementsLoaded: boolean;
    partnerCodesLoaded: boolean;
    partnerLinksLoaded: boolean;
    partnerDataLoaded: boolean;
    partnerDataError?: string;
    ticketsLoaded: boolean;
    tradingStreakLoaded: boolean;
    notificationsLoaded: boolean;
    activityFeedLoaded: boolean;
    levelStatesLoaded: boolean;
    levelTiersLoaded: boolean;
    payoutMethodSettingsLoaded: boolean;
    walletPayoutsLoaded: boolean;
  };

  subscribe: (topic: string, filters?: Record<string, any>) => Promise<void>;

  registerOutboxHandler: (handlers: {
    upsertTickets?: (list: Ticket[]) => void;
    removeTickets?: (ids: string[]) => void;
  }) => () => void;

  ensureUser: () => Promise<void>;
  ensureTradingAccounts: () => Promise<void>;
  ensureFundedPayouts: () => Promise<void>;
  ensureInvoices: () => Promise<void>;
  ensureFundedContracts: () => Promise<void>;
  ensureWalletBalances: () => Promise<void>;
  ensureAchievements: () => Promise<void>;
  ensureTrades: () => Promise<void>;
  ensureCommissions: () => Promise<void>;
  ensureSettlements: () => Promise<void>;
  ensurePartnerCodes: () => Promise<void>;
  ensurePartnerLinks: () => Promise<void>;
  ensurePartnerData: () => Promise<void>;
  ensureTickets: () => Promise<void>;
  ensureTradingStreak: () => Promise<void>;
  ensureWalletTransactions: () => Promise<void>;
  ensureWalletBuckets: () => Promise<void>;
  reloadWalletSummaries: () => Promise<void>;
  ensureNotifications: () => Promise<void>;
  ensureActivityFeed: () => Promise<void>;
  ensureLevelTiers: () => Promise<void>;
  refreshLevelTiers: () => Promise<void>;
};

/**********************
 * Internal State (Reducer)
 **********************/
type DataState = {
  user: User | null;
  tradingAccounts: Record<string, TradingAccount>;
  fundedPayouts: Record<string, FundedPayout>;
  invoices: Record<string, Invoice>;
  fundedContracts: Record<string, FundedContract>;
  walletBalances: WalletSummaryBalance[];
  walletTransactions: WalletTransaction[];
  walletBuckets: any[];
  achievements: Record<string, AchievementLite>;
  trades: Record<string, any>;
  commissions: Record<string, Commission>;
  settlements: Record<string, Settlement>;
  partnerCodes: Record<string, PartnerCode>;
  partnerLinks: Record<string, PartnerLink>;
  partnerData: PartnerData | null;
  tickets: Record<string, Ticket>;
  tradingStreak: TradingStreak | null;
  notifications: Record<string, Notification>;
  activityFeed: ActivityFeedItem[];
  levelStatesBySeasonId: Record<string, LevelState>;
  levelTiers: LevelRankTier[];
  payoutMethodSettings: PayoutMethodSetting[];
  walletPayouts: any[];
};

const initialDataState: DataState = {
  user: null,
  tradingAccounts: {},
  fundedPayouts: {},
  invoices: {},
  fundedContracts: {},
  walletBalances: [],
  walletTransactions: [],
  walletBuckets: [],
  achievements: {},
  trades: {},
  commissions: {},
  settlements: {},
  partnerCodes: {},
  partnerLinks: {},
  partnerData: null,
  tickets: {},
  tradingStreak: null,
  notifications: {},
  activityFeed: [],
  levelStatesBySeasonId: {},
  levelTiers: [],
  payoutMethodSettings: [],
  walletPayouts: [],
};

type Action =
  | { type: "reset" }
  | { type: "user/set"; user: User | null }
  | { type: "user/merge"; partial: Partial<User> }
  | { type: "user/upsertAddress"; address: Address }
  | { type: "user/updatePayoutMethods"; payout_methods: PayoutMethod[] }
  | { type: "user/upsertPayoutMethod"; method: PayoutMethod }
  | { type: "user/removePayoutMethod"; methodId: string }
  | { type: "tradingAccounts/upsert"; list: TradingAccount[] }
  | { type: "tradingAccounts/remove"; ids: string[] }
  | { type: "fundedPayouts/upsert"; list: FundedPayout[] }
  | { type: "fundedPayouts/remove"; ids: string[] }
  | { type: "invoices/set"; list: Invoice[] }
  | { type: "invoices/upsert"; list: Invoice[] }
  | { type: "fundedContracts/set"; list: FundedContract[] }
  | { type: "fundedContracts/upsert"; list: FundedContract[] }
  | { type: "walletBalances/set"; list: WalletSummaryBalance[] }
  | { type: "walletTransactions/set"; list: WalletTransaction[] }
  | { type: "walletTransactions/upsert"; list: WalletTransaction[] }
  | { type: "walletTransactions/remove"; ids: string[] }
  | { type: "walletBuckets/set"; list: any[] }
  | { type: "achievements/upsert"; list: AchievementLite[] }
  | { type: "achievements/remove"; ids: string[] }
  | { type: "trades/upsert"; list: any[] }
  | { type: "trades/remove"; ids: string[] }
  | { type: "commissions/upsert"; list: Commission[] }
  | { type: "commissions/remove"; ids: string[] }
  | { type: "settlements/upsert"; list: Settlement[] }
  | { type: "settlements/remove"; ids: string[] }
  | { type: "partnerCodes/upsert"; list: PartnerCode[] }
  | { type: "partnerCodes/remove"; ids: string[] }
  | { type: "partnerLinks/upsert"; list: PartnerLink[] }
  | { type: "partnerLinks/remove"; ids: string[] }
  | { type: "partnerData/set"; data: PartnerData | null }
  | { type: "partnerData/update"; handle: string; partner_display_name: string }
  | { type: "tickets/upsert"; list: Ticket[] }
  | { type: "tickets/remove"; ids: string[] }
  | { type: "tradingStreak/set"; data: TradingStreak | null }
  | { type: "notifications/upsert"; list: Notification[] }
  | { type: "notifications/remove"; ids: string[] }
  | { type: "activityFeed/set"; list: ActivityFeedItem[] }
  | { type: "levels/states/upsert"; list: LevelState[] }
  | { type: "levels/states/remove"; ids: string[] }
  | { type: "levels/tiers/set"; list: LevelRankTier[] }
  | { type: "payoutMethodSettings/set"; list: PayoutMethodSetting[] }
  | { type: "walletPayouts/set"; list: any[] };

const upsertById = <T extends { id?: string }>(
  target: Record<string, any>,
  list: T[],
  key: (t: T) => string
) => {
  const next = { ...target } as Record<string, any>;
  for (const item of list) {
    const id = key(item);
    next[id] = { ...(next[id] || {}), ...item };
  }
  return next;
};

function reducer(state: DataState, action: Action): DataState {
  switch (action.type) {
    case "reset":
      return initialDataState;

    case "user/set":
      return { ...state, user: action.user };

    case "user/merge":
      if (!state.user) return state;
      return { ...state, user: { ...state.user, ...action.partial } };

    case "user/upsertAddress": {
      if (!state.user) return state;
      const existing = state.user.addresses ?? [];
      const idx = existing.findIndex((a) => a.id === action.address.id);
      const updated =
        idx >= 0
          ? existing.map((a, i) =>
              i === idx ? { ...a, ...action.address } : a
            )
          : [...existing, action.address];
      return { ...state, user: { ...state.user, addresses: updated } };
    }

    case "user/updatePayoutMethods":
      if (!state.user) return state;
      return {
        ...state,
        user: { ...state.user, payout_methods: action.payout_methods },
      };

    case "user/upsertPayoutMethod": {
      if (!state.user) return state;
      const currentMethods = state.user.payout_methods || [];
      const updatedMethod = action.method;

      const existingIndex = currentMethods.findIndex(
        (m) => m.id === updatedMethod.id
      );

      let newMethods: PayoutMethod[];
      if (existingIndex >= 0) {
        newMethods = [...currentMethods];
        newMethods[existingIndex] = updatedMethod;
      } else {
        newMethods = [...currentMethods, updatedMethod];
      }

      if (updatedMethod.is_default) {
        newMethods = newMethods.map((m) =>
          m.id === updatedMethod.id ? m : { ...m, is_default: false }
        );
      }

      return { ...state, user: { ...state.user, payout_methods: newMethods } };
    }

    case "user/removePayoutMethod": {
      if (!state.user) return state;
      const currentMethods = state.user.payout_methods || [];
      const newMethods = currentMethods.filter((m) => m.id !== action.methodId);

      return { ...state, user: { ...state.user, payout_methods: newMethods } };
    }

    case "tradingAccounts/upsert": {
      const merged = upsertById(
        state.tradingAccounts,
        action.list,
        (x) => x.id!
      );
      for (const item of action.list) {
        const id = item.id;
        if (!id || !item.statsDailyLatest) continue;
        const acct = merged[id];
        if (!acct) continue;
        const existing: any[] = Array.isArray(acct.statsDaily) ? acct.statsDaily : [];
        const day = item.statsDailyLatest.trading_day_utc;
        if (day) {
          const idx = existing.findIndex((e: any) => e.trading_day_utc === day);
          if (idx >= 0) {
            const updated = [...existing];
            updated[idx] = { ...existing[idx], ...item.statsDailyLatest };
            merged[id] = { ...acct, statsDaily: updated };
          } else {
            merged[id] = { ...acct, statsDaily: [...existing, item.statsDailyLatest] };
          }
        }
      }
      return { ...state, tradingAccounts: merged };
    }

    case "tradingAccounts/remove": {
      const next = { ...state.tradingAccounts };
      action.ids.forEach((id) => delete next[id]);
      return { ...state, tradingAccounts: next };
    }

    case "fundedPayouts/upsert":
      return {
        ...state,
        fundedPayouts: upsertById(state.fundedPayouts, action.list, (x) => x.id!),
      };

    case "fundedPayouts/remove": {
      const next = { ...state.fundedPayouts };
      action.ids.forEach((id) => delete next[id]);
      return { ...state, fundedPayouts: next };
    }

    case "invoices/set": {
      const next: Record<string, Invoice> = {};
      for (const inv of action.list) next[inv.invoiceId] = inv;
      return { ...state, invoices: next };
    }

    case "invoices/upsert": {
      const next = { ...state.invoices };
      for (const inv of action.list) {
        next[inv.invoiceId] = { ...(next[inv.invoiceId] || {}), ...inv };
      }
      return { ...state, invoices: next };
    }

    case "fundedContracts/set": {
      const next: Record<string, FundedContract> = {};
      for (const c of action.list) next[c.id] = c;
      return { ...state, fundedContracts: next };
    }

    case "fundedContracts/upsert":
      return {
        ...state,
        fundedContracts: upsertById(
          state.fundedContracts,
          action.list,
          (x) => x.id!
        ),
      };

    case "walletBalances/set":
      return { ...state, walletBalances: action.list };

    case "walletTransactions/set":
      return { ...state, walletTransactions: action.list };

    case "walletTransactions/upsert": {
      const existing = new Map(state.walletTransactions.map((t) => [t.id, t]));
      for (const tx of action.list) {
        existing.set(tx.id, { ...(existing.get(tx.id) || {}), ...tx });
      }
      return { ...state, walletTransactions: Array.from(existing.values()) };
    }

    case "walletTransactions/remove": {
      const removeSet = new Set(action.ids);
      return {
        ...state,
        walletTransactions: state.walletTransactions.filter(
          (t) => !removeSet.has(t.id)
        ),
      };
    }

    case "walletBuckets/set":
      return { ...state, walletBuckets: action.list };

    case "achievements/upsert":
      return {
        ...state,
        achievements: upsertById(state.achievements, action.list, (x) => x.id!),
      };

    case "achievements/remove": {
      const next = { ...state.achievements };
      action.ids.forEach((id) => delete next[id]);
      return { ...state, achievements: next };
    }

    case "trades/upsert":
      return {
        ...state,
        trades: upsertById(state.trades, action.list, (x) => x.id!),
      };

    case "trades/remove": {
      const next = { ...state.trades };
      action.ids.forEach((id) => delete next[id]);
      return { ...state, trades: next };
    }

    case "commissions/upsert":
      return {
        ...state,
        commissions: upsertById(state.commissions, action.list, (x) => x.id!),
      };

    case "commissions/remove": {
      const next = { ...state.commissions };
      action.ids.forEach((id) => delete next[id]);
      return { ...state, commissions: next };
    }

    case "settlements/upsert":
      return {
        ...state,
        settlements: upsertById(state.settlements, action.list, (x) => x.id!),
      };

    case "settlements/remove": {
      const next = { ...state.settlements };
      action.ids.forEach((id) => delete next[id]);
      return { ...state, settlements: next };
    }

    case "partnerCodes/upsert":
      return {
        ...state,
        partnerCodes: upsertById(state.partnerCodes, action.list, (x) => x.id!),
      };

    case "partnerCodes/remove": {
      const next = { ...state.partnerCodes };
      action.ids.forEach((id) => delete next[id]);
      return { ...state, partnerCodes: next };
    }

    case "partnerLinks/upsert":
      return {
        ...state,
        partnerLinks: upsertById(state.partnerLinks, action.list, (x) => x.id!),
      };

    case "partnerLinks/remove": {
      const next = { ...state.partnerLinks };
      action.ids.forEach((id) => delete next[id]);
      return { ...state, partnerLinks: next };
    }

    case "partnerData/set":
      return { ...state, partnerData: action.data };

    case "partnerData/update": {
      if (!state.partnerData) return state;
      const updatedPartnerData = {
        ...state.partnerData,
        handle: action.handle,
        partner_display_name: action.partner_display_name,
      };
      return { ...state, partnerData: updatedPartnerData };
    }

    case "tickets/upsert":
      return {
        ...state,
        tickets: upsertById(state.tickets, action.list, (x) => x.ticket_id!),
      };

    case "tickets/remove": {
      const next = { ...state.tickets };
      action.ids.forEach((id) => delete next[id]);
      return { ...state, tickets: next };
    }

    case "tradingStreak/set":
      return { ...state, tradingStreak: action.data };

    case "notifications/upsert":
      return {
        ...state,
        notifications: upsertById(
          state.notifications,
          action.list,
          (x) => x.id!
        ),
      };

    case "notifications/remove": {
      const next = { ...state.notifications };
      action.ids.forEach((id) => delete next[id]);
      return { ...state, notifications: next };
    }

    case "activityFeed/set":
      return { ...state, activityFeed: action.list };

    case "levels/states/upsert": {
      const next = { ...state.levelStatesBySeasonId } as Record<string, LevelState>;
      for (const s of action.list) {
        next[s.season_id] = { ...(next[s.season_id] || {}), ...s };
      }
      return { ...state, levelStatesBySeasonId: next };
    }

    case "levels/states/remove": {
      const next = { ...state.levelStatesBySeasonId };
      action.ids.forEach((id) => delete next[id]);
      return { ...state, levelStatesBySeasonId: next };
    }

    case "levels/tiers/set":
      return { ...state, levelTiers: action.list };

    case "payoutMethodSettings/set":
      return { ...state, payoutMethodSettings: action.list };

    case "walletPayouts/set":
      return { ...state, walletPayouts: action.list };

    default:
      return state;
  }
}

/**********************
 * Provider
 **********************/
const WebSocketContext = createContext<WebSocketContextType | undefined>(
  undefined
);

export const WebSocketProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, isAuthLoading } = useAuth();
  const isAuthenticatedRef = useRef(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;

  const [status, setStatus] = React.useState<ConnectionStatus>("closed");
  const [reconnectTrigger, setReconnectTrigger] = React.useState(0);
  const [data, dispatch] = useReducer(reducer, initialDataState);

  // meta refs (not in reducer → avoid renders)
  const userMeta = useRef<{ loaded: boolean }>({ loaded: false });
  const taMeta = useRef<{ loaded: boolean; lastSeq?: number }>({ loaded: false });
  const fpMeta = useRef<{ loaded: boolean; lastSeq?: number }>({ loaded: false });
  const invoicesMeta = useRef<{ loaded: boolean; lastSeq?: number }>({
    loaded: false,
  });
  const fundedContractsMeta = useRef<{ loaded: boolean; lastSeq?: number }>({
    loaded: false,
  });
  const walletBalancesMeta = useRef<{ loaded: boolean }>({ loaded: false });
  const walletTransactionsMeta = useRef<{
    loaded: boolean;
    lastSeq?: number;
  }>({ loaded: false });
  const walletBucketsMeta = useRef<{ loaded: boolean }>({ loaded: false });
  const achMeta = useRef<{ loaded: boolean; lastSeq?: number }>({ loaded: false });
  const tradesMeta = useRef<{ loaded: boolean; lastSeq?: number }>({
    loaded: false,
  });
  const commissionsMeta = useRef<{ loaded: boolean; lastSeq?: number }>({
    loaded: false,
  });
  const settlementsMeta = useRef<{ loaded: boolean; lastSeq?: number }>({
    loaded: false,
  });
  const partnerCodesMeta = useRef<{ loaded: boolean; lastSeq?: number }>({
    loaded: false,
  });
  const partnerLinksMeta = useRef<{ loaded: boolean; lastSeq?: number }>({
    loaded: false,
  });
  const partnerDataMeta = useRef<{ loaded: boolean }>({ loaded: false });
  const ticketsMeta = useRef<{ loaded: boolean; lastSeq?: number }>({
    loaded: false,
  });
  const tradingStreakMeta = useRef<{ loaded: boolean }>({ loaded: false });
  const notificationsMeta = useRef<{ loaded: boolean; lastSeq?: number }>({
    loaded: false,
  });
  const activityFeedMeta = useRef<{ loaded: boolean }>({ loaded: false });
  const levelsMeta = useRef<{ statesLoaded: boolean; eventsLoaded: boolean }>({
    statesLoaded: false,
    eventsLoaded: false,
  });
  const levelTiersMeta = useRef<{ loaded: boolean }>({ loaded: false });
  const payoutMethodSettingsMeta = useRef<{ loaded: boolean }>({
    loaded: false,
  });
  const walletPayoutsMeta = useRef<{ loaded: boolean }>({ loaded: false });

  // socket + timers
  const socketRef = useRef<WebSocket | null>(null);
  const inflightRef = useRef<
    Map<string, { resolve: Function; reject: Function; to: number }>
  >(new Map());
  const chunkBufferRef = useRef<Map<string, ChunkRequestAccumulator>>(new Map());
  const patchChunkBufferRef = useRef<Map<string, PatchV2RequestAccumulator>>(new Map());
  const pendingQueueRef = useRef<Array<{ msg: any; resolve: Function; reject: Function; timeoutMs: number; action: string }>>([]);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<number | null>(null);
  const suppressReconnectRef = useRef(false);
  const lastIdentityRef = useRef("");
  const startedKeyRef = useRef<string | null>(null);
  const hbRef = useRef<number | null>(null);
  const watchdogRef = useRef<number | null>(null);
  const connectRef = useRef<(() => void) | null>(null);
  const clientInitiatedCloseRef = useRef(false);

  // heartbeat health
  const lastPongAtRef = useRef<number>(Date.now());
  const lastPingSentAtRef = useRef<number>(0);
  const missedPongsRef = useRef<number>(0);
  const connectionHealthRef = useRef<{
    pingSent: number;
    pongReceived: number;
    avgLatency: number;
  }>({
    pingSent: 0,
    pongReceived: 0,
    avgLatency: 0,
  });

  /**********************
   * Utilities
   **********************/
  const handleAuthenticationError = useCallback(() => {
    window.dispatchEvent(new CustomEvent("app:session-expired"));
  }, []);

  const resolveWsBase = () => {
    return WS_BASE;
  };

  const rawSend = (obj: any) => {
    const s = socketRef.current;
    if (s && s.readyState === WebSocket.OPEN) {
      s.send(JSON.stringify(obj));
      return true;
    }
    return false;
  };

  const sendMessage = useCallback((data: any) => {
    rawSend(data);
  }, []);

  const publish = useCallback((action: string, payload?: any) => {
    rawSend({ action, payload });
  }, []);

  const toPositiveInt = (value: unknown, fallback = 1): number => {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return fallback;
    return Math.floor(n);
  };

  const clonePayload = <T,>(value: T): T => {
    if (typeof structuredClone === "function") return structuredClone(value);
    return JSON.parse(JSON.stringify(value));
  };

  const normalizeChunkPath = (path: unknown): ChunkPathSegment[] => {
    if (!Array.isArray(path)) return [];
    return path.map((seg) => (typeof seg === "number" ? seg : String(seg)));
  };

  const getRequestChunkAccumulator = (requestId: string): ChunkRequestAccumulator => {
    let acc = chunkBufferRef.current.get(requestId);
    if (!acc) {
      acc = { sequence: 0, groups: new Map() };
      chunkBufferRef.current.set(requestId, acc);
    }
    return acc;
  };

  const ensureChunkGroup = (
    acc: ChunkRequestAccumulator,
    groupId: string,
    partial?: {
      parentId?: string | null;
      level?: number;
      count?: number;
      path?: ChunkPathSegment[];
      parentIndex?: number;
      firstSeq?: number;
    }
  ): ChunkGroupAccumulator => {
    let group = acc.groups.get(groupId);
    if (!group) {
      group = {
        id: groupId,
        parentId: partial?.parentId ?? null,
        level: partial?.level ?? 0,
        count: partial?.count ?? 0,
        path: partial?.path ?? [],
        parentIndex: partial?.parentIndex ?? 0,
        firstSeq: partial?.firstSeq ?? Number.MAX_SAFE_INTEGER,
        parts: new Map(),
        childGroupIds: new Set(),
        childGroupByIndex: new Map(),
        completed: null,
      };
      acc.groups.set(groupId, group);
      return group;
    }

    if (partial?.parentId !== undefined) group.parentId = partial.parentId;
    if (partial?.level !== undefined) group.level = partial.level;
    if (partial?.count !== undefined && partial.count > 0) group.count = partial.count;
    if (partial?.path && partial.path.length > 0) group.path = partial.path;
    if (partial?.parentIndex !== undefined && partial.parentIndex > 0) {
      group.parentIndex = partial.parentIndex;
    }
    if (partial?.firstSeq !== undefined) {
      group.firstSeq = Math.min(group.firstSeq, partial.firstSeq);
    }

    return group;
  };

  const getNestedValue = (obj: any, path: ChunkPathSegment[]): any => {
    let current = obj;
    for (const segment of path) {
      if (current == null) return undefined;
      current = current[segment as any];
    }
    return current;
  };

  const setNestedValue = (obj: any, path: ChunkPathSegment[], value: any): boolean => {
    if (!obj || !path.length) return false;
    let current = obj;
    for (let i = 0; i < path.length - 1; i++) {
      const segment = path[i];
      if (current == null || !(segment in current)) return false;
      current = current[segment as any];
    }
    const last = path[path.length - 1];
    if (current == null) return false;
    current[last as any] = value;
    return true;
  };

  const mergeChunkPart = (
    baseMessage: any,
    partMessage: any,
    path: ChunkPathSegment[]
  ): any => {
    if (!path.length) return baseMessage;
    const baseData = baseMessage?.data;
    const partData = partMessage?.data;
    if (!baseData || !partData) return baseMessage;

    const baseItems = getNestedValue(baseData, path);
    const partItems = getNestedValue(partData, path);
    if (!Array.isArray(baseItems) || !Array.isArray(partItems)) return baseMessage;

    setNestedValue(baseData, path, [...baseItems, ...partItems]);
    return baseMessage;
  };

  const assembleChunkGroup = (
    group: ChunkGroupAccumulator,
    partsMap: Map<number, any>
  ): any | null => {
    if (!group.path.length) return null;
    const orderedIndexes = Array.from(partsMap.keys()).sort((a, b) => a - b);
    if (!orderedIndexes.length) return null;

    let merged = clonePayload(partsMap.get(orderedIndexes[0]));
    for (let i = 1; i < orderedIndexes.length; i++) {
      const part = partsMap.get(orderedIndexes[i]);
      merged = mergeChunkPart(merged, part, group.path);
    }

    if (merged && typeof merged === "object") {
      delete merged.chunk;
    }
    return merged;
  };

  const getPatchV2Accumulator = (requestId: string): PatchV2RequestAccumulator => {
    let acc = patchChunkBufferRef.current.get(requestId);
    if (!acc) {
      acc = {
        rootId: null,
        basePayload: null,
        expectedGroupCount: 0,
        pendingComplete: false,
        groups: new Map(),
        appliedGroupIds: new Set(),
      };
      patchChunkBufferRef.current.set(requestId, acc);
    }
    return acc;
  };

  const ensurePatchV2Group = (
    acc: PatchV2RequestAccumulator,
    groupId: string,
    partial?: {
      path?: ChunkPathSegment[];
      count?: number;
    }
  ): PatchV2GroupAccumulator => {
    let group = acc.groups.get(groupId);
    if (!group) {
      group = {
        id: groupId,
        path: partial?.path ?? [],
        count: partial?.count ?? 0,
        parts: new Map(),
      };
      acc.groups.set(groupId, group);
      return group;
    }

    if (partial?.path) group.path = partial.path;
    if (partial?.count !== undefined && partial.count > 0) group.count = partial.count;
    return group;
  };

  const appendItemsAtChunkPath = (
    basePayload: any,
    path: ChunkPathSegment[],
    items: any[]
  ): boolean => {
    if (!basePayload || !Array.isArray(items)) return false;
    if (!path.length) {
      if (!Array.isArray(basePayload.data)) return false;
      basePayload.data = [...basePayload.data, ...items];
      return true;
    }

    const baseData = basePayload.data;
    if (baseData == null) return false;
    const current = getNestedValue(baseData, path);
    if (!Array.isArray(current)) return false;
    return setNestedValue(baseData, path, [...current, ...items]);
  };

  const collectPatchV2GroupItems = (group: PatchV2GroupAccumulator): any[] | null => {
    if (!group.count) return null;
    const merged: any[] = [];
    for (let i = 1; i <= group.count; i++) {
      const partItems = group.parts.get(i);
      if (!partItems) return null;
      merged.push(...clonePayload(partItems));
    }
    return merged;
  };

  const flushReadyPatchV2Groups = (acc: PatchV2RequestAccumulator): boolean => {
    if (!acc.basePayload) return false;

    let progressed = false;
    let loopProgress = true;
    while (loopProgress) {
      loopProgress = false;
      const readyGroups = Array.from(acc.groups.values())
        .filter((group) => !acc.appliedGroupIds.has(group.id))
        .map((group) => ({ group, items: collectPatchV2GroupItems(group) }))
        .filter((entry): entry is { group: PatchV2GroupAccumulator; items: any[] } => Array.isArray(entry.items))
        .sort((a, b) => {
          if (a.group.path.length !== b.group.path.length) {
            return a.group.path.length - b.group.path.length;
          }
          return a.group.id.localeCompare(b.group.id);
        });

      for (const entry of readyGroups) {
        if (!appendItemsAtChunkPath(acc.basePayload, entry.group.path, entry.items)) {
          continue;
        }
        acc.appliedGroupIds.add(entry.group.id);
        loopProgress = true;
        progressed = true;
      }
    }

    return progressed;
  };

  const tryFinalizePatchV2Request = (
    requestId: string,
    acc: PatchV2RequestAccumulator
  ): any | null => {
    if (!acc.basePayload) return null;

    flushReadyPatchV2Groups(acc);

    if (!acc.pendingComplete) return null;
    if (acc.appliedGroupIds.size < acc.expectedGroupCount) return null;

    const finalized = clonePayload(acc.basePayload);
    if (finalized && typeof finalized === "object") {
      delete finalized.chunk;
    }
    patchChunkBufferRef.current.delete(requestId);
    return finalized;
  };

  const processIncomingPatchV2 = (payload: any, chunk: ChunkMeta): any | null => {
    const requestId = String(payload?.requestId ?? "");
    if (!requestId) return payload;

    const acc = getPatchV2Accumulator(requestId);
    const rootId =
      chunk.rootId === null || chunk.rootId === undefined
        ? null
        : String(chunk.rootId);

    if (rootId && acc.rootId && acc.rootId !== rootId) {
      patchChunkBufferRef.current.delete(requestId);
      return processIncomingPatchV2(payload, chunk);
    }
    if (rootId) acc.rootId = rootId;

    const kind = String(chunk.kind ?? "patch");
    const groupCount = toPositiveInt(chunk.groupCount, 0);
    if (kind === "base") {
      acc.basePayload = clonePayload(payload);
      if (acc.basePayload && typeof acc.basePayload === "object") {
        delete acc.basePayload.chunk;
      }
      acc.expectedGroupCount = groupCount;
      return tryFinalizePatchV2Request(requestId, acc);
    }

    if (kind === "complete") {
      acc.pendingComplete = true;
      if (groupCount > 0) acc.expectedGroupCount = groupCount;
      return tryFinalizePatchV2Request(requestId, acc);
    }

    if (kind !== "patch") {
      return payload;
    }

    const chunkId = String(chunk.id ?? "");
    if (!chunkId) return null;

    const index = toPositiveInt(chunk.index, 1);
    const count = toPositiveInt(chunk.count, 1);
    const path = normalizeChunkPath(chunk.path);
    const items = Array.isArray(payload?.items) ? clonePayload(payload.items) : null;
    if (!items) return null;

    const group = ensurePatchV2Group(acc, chunkId, { path, count });
    if (!group.parts.has(index)) {
      group.parts.set(index, items);
    }

    return tryFinalizePatchV2Request(requestId, acc);
  };

  const tryFinalizeChunkGroup = (
    acc: ChunkRequestAccumulator,
    groupId: string
  ): any | null => {
    const group = acc.groups.get(groupId);
    if (!group) return null;
    if (group.completed) return group.completed;
    if (!group.count || !group.path.length) return null;

    const combinedParts = new Map(group.parts);

    let missingIndexes: number[] = [];
    for (let i = 1; i <= group.count; i++) {
      if (!combinedParts.has(i)) missingIndexes.push(i);
    }

    if (missingIndexes.length) {
      const consumedChildIds = new Set<string>();

      // First, map children to explicit parent indexes when available.
      for (const idx of missingIndexes) {
        const mappedChildId = group.childGroupByIndex.get(idx);
        if (!mappedChildId || consumedChildIds.has(mappedChildId)) continue;
        const child = acc.groups.get(mappedChildId);
        if (!child?.completed) continue;
        combinedParts.set(idx, clonePayload(child.completed));
        consumedChildIds.add(mappedChildId);
      }

      missingIndexes = [];
      for (let i = 1; i <= group.count; i++) {
        if (!combinedParts.has(i)) missingIndexes.push(i);
      }

      // Fallback for legacy payloads without parentIndex metadata.
      if (missingIndexes.length) {
        const completedChildren = Array.from(group.childGroupIds)
          .filter((childId) => !consumedChildIds.has(childId))
          .map((childId) => acc.groups.get(childId))
          .filter((child): child is ChunkGroupAccumulator => !!child?.completed)
          .sort((a, b) => a.firstSeq - b.firstSeq);

        const assignable = Math.min(missingIndexes.length, completedChildren.length);
        for (let i = 0; i < assignable; i++) {
          const idx = missingIndexes[i];
          if (!combinedParts.has(idx)) {
            combinedParts.set(idx, clonePayload(completedChildren[i].completed));
          }
        }
      }
    }

    for (let i = 1; i <= group.count; i++) {
      if (!combinedParts.has(i)) return null;
    }

    const assembled = assembleChunkGroup(group, combinedParts);
    if (!assembled) return null;
    group.completed = assembled;
    return assembled;
  };

  const advanceChunkAssembly = (
    acc: ChunkRequestAccumulator,
    startGroupId: string
  ): any | null => {
    let currentId: string | null = startGroupId;
    while (currentId) {
      const assembled = tryFinalizeChunkGroup(acc, currentId);
      if (!assembled) return null;

      const current = acc.groups.get(currentId);
      if (!current?.parentId) {
        return assembled;
      }

      currentId = current.parentId;
    }
    return null;
  };

  const processIncomingChunk = (payload: any): any | null => {
    const chunk = payload?.chunk as ChunkMeta | undefined;
    if (!chunk || typeof chunk !== "object") return payload;

    if (chunk.transport === "patch_v2") {
      return processIncomingPatchV2(payload, chunk);
    }

    const requestId = String(payload?.requestId ?? "");
    if (!requestId) return payload;

    const count = toPositiveInt(chunk.count, 1);
    if (count <= 1) {
      const single = clonePayload(payload);
      if (single && typeof single === "object") delete single.chunk;
      return single;
    }

    const chunkId = String(chunk.id ?? "");
    if (!chunkId) return payload;

    const acc = getRequestChunkAccumulator(requestId);
    const arrivalSeq = ++acc.sequence;

    const index = toPositiveInt(chunk.index, 1);
    const path = normalizeChunkPath(chunk.path);
    const level = toPositiveInt(chunk.level, 0);
    const parentIndex = toPositiveInt(chunk.parentIndex, 0);
    const parentId =
      chunk.parentId === null || chunk.parentId === undefined
        ? null
        : String(chunk.parentId);
    const hasParentParentId = chunk.parentParentId !== undefined;
    const parentParentId =
      chunk.parentParentId === null || chunk.parentParentId === undefined
        ? null
        : String(chunk.parentParentId);
    const hasParentParentIndex = chunk.parentParentIndex !== undefined;
    const parentParentIndex = toPositiveInt(chunk.parentParentIndex, 0);
    const hasParentCount = chunk.parentCount !== undefined;
    const parentCount = toPositiveInt(chunk.parentCount, 0);
    const hasParentPath = chunk.parentPath !== undefined;
    const parentPath = normalizeChunkPath(chunk.parentPath);
    const hasParentLevel = chunk.parentLevel !== undefined;
    const parentLevel = toPositiveInt(chunk.parentLevel, 0);

    const group = ensureChunkGroup(acc, chunkId, {
      parentId,
      level,
      count,
      path,
      parentIndex,
      firstSeq: arrivalSeq,
    });

    if (parentId) {
      const parentMeta: {
        parentId?: string | null;
        parentIndex?: number;
        count?: number;
        path?: ChunkPathSegment[];
        level?: number;
      } = {};
      if (hasParentParentId) parentMeta.parentId = parentParentId;
      if (hasParentParentIndex && parentParentIndex > 0) {
        parentMeta.parentIndex = parentParentIndex;
      }
      if (hasParentCount && parentCount > 0) parentMeta.count = parentCount;
      if (hasParentPath && parentPath.length > 0) parentMeta.path = parentPath;
      if (hasParentLevel) parentMeta.level = parentLevel;

      const parent = ensureChunkGroup(acc, parentId, parentMeta);
      parent.childGroupIds.add(group.id);
      if (group.parentIndex > 0) {
        parent.childGroupByIndex.set(group.parentIndex, group.id);
      }

      // If this child chunk carries grandparent metadata, stitch the
      // reconstructed parent group into the grandparent as well.
      if (parent.parentId) {
        const grandparent = ensureChunkGroup(acc, parent.parentId);
        grandparent.childGroupIds.add(parent.id);
        if (parent.parentIndex > 0) {
          grandparent.childGroupByIndex.set(parent.parentIndex, parent.id);
        }
      }
    }

    if (!group.parts.has(index)) {
      group.parts.set(index, clonePayload(payload));
    }

    const assembledRoot = advanceChunkAssembly(acc, group.id);
    if (!assembledRoot) return null;
    if (assembledRoot && typeof assembledRoot === "object") {
      delete assembledRoot.chunk;
    }
    if (acc.groups.size > 0) {
      chunkBufferRef.current.delete(requestId);
      patchChunkBufferRef.current.delete(requestId);
    }
    return assembledRoot;
  };

  const sendOrQueue = useCallback(
    <T,>(action: string, msg: any, requestId: string, timeoutMs: number, resolve: (v: T) => void, reject: (r: any) => void) => {
      let to: number | null = null;
      if (timeoutMs > 0) {
        to = window.setTimeout(() => {
          inflightRef.current.delete(requestId);
          chunkBufferRef.current.delete(requestId);
          patchChunkBufferRef.current.delete(requestId);
          pendingQueueRef.current = pendingQueueRef.current.filter(
            (q) => q.msg.requestId !== requestId
          );
          console.error(`[WS TIMEOUT] ${action}`, { requestId, timeoutMs });
          reject(new Error(`Request timeout: ${action}`));
        }, timeoutMs) as unknown as number;
      }

      const wrappedResolve = (value: T) => {
        console.log(`[WS RESPONSE] ${action}`, { requestId, data: value });
        resolve(value as T);
      };
      const wrappedReject = (reason: any) => {
        const isExpectedClose =
          reason instanceof Error &&
          reason.message === "Connection closed" &&
          (suppressReconnectRef.current || clientInitiatedCloseRef.current);
        if (isExpectedClose) {
          console.log(`[WS CLOSED] ${action}`, { requestId, reason: reason.message });
        } else {
          console.error(`[WS ERROR] ${action}`, { requestId, error: reason });
        }
        reject(reason);
      };

      inflightRef.current.set(requestId, { resolve: wrappedResolve, reject: wrappedReject, to: to ?? 0 });

      if (!rawSend(msg)) {
        console.log(`[WS QUEUED] ${action} - socket not yet open, queuing`, { requestId });
        pendingQueueRef.current.push({ msg, resolve: wrappedResolve, reject: wrappedReject, timeoutMs, action });
      }
    },
    []
  );

  const flushPendingQueue = useCallback(() => {
    const queue = [...pendingQueueRef.current];
    pendingQueueRef.current = [];
    for (const item of queue) {
      if (!inflightRef.current.has(item.msg.requestId)) {
        let to: number | null = null;
        if (item.timeoutMs > 0) {
          to = window.setTimeout(() => {
            inflightRef.current.delete(item.msg.requestId);
            chunkBufferRef.current.delete(item.msg.requestId);
            patchChunkBufferRef.current.delete(item.msg.requestId);
            item.reject(new Error(`Request timeout: ${item.action}`));
          }, item.timeoutMs) as unknown as number;
        }
        inflightRef.current.set(item.msg.requestId, { resolve: item.resolve, reject: item.reject, to: to ?? 0 });
      }
      if (!rawSend(item.msg)) {
        console.error(`[WS QUEUE FLUSH FAIL] ${item.action} - socket still not open`, { requestId: item.msg.requestId });
        const inflight = inflightRef.current.get(item.msg.requestId);
        if (inflight) {
          clearTimeout(inflight.to);
          inflightRef.current.delete(item.msg.requestId);
          chunkBufferRef.current.delete(item.msg.requestId);
          patchChunkBufferRef.current.delete(item.msg.requestId);
          inflight.reject(new Error(`Socket not open: ${item.action}`));
        }
      } else {
        console.log(`[WS QUEUE FLUSH] ${item.action} sent`, { requestId: item.msg.requestId });
      }
    }
  }, []);

  const request = useCallback(
    <T,>(action: string, payload?: any, timeoutMs = DEFAULT_TIMEOUT) => {
      const requestId = genId();
      const msg = { action, payload, requestId };
      console.log(`[WS REQUEST] ${action}`, { requestId, payload });

      return new Promise<T>((resolve, reject) => {
        sendOrQueue(action, msg, requestId, timeoutMs, resolve, reject);
      });
    },
    [sendOrQueue]
  );

  const { subscribeTopic } = useMemo(() => makeSubscribeApi(request), [request]);

  const requestJSON = useCallback(
    async (action: string, payload: any, timeout: number) => {
      const res: any = await request(action, payload, timeout);
      return res?.data ?? res ?? {};
    },
    [request]
  );

  // File URL cache
  const fileUrlCacheRef = useRef<
    Map<string, { url: string; expAt: number; etag?: string }>
  >(new Map());

  const getFileDownloadUrl = useCallback(
    async (
      fileId: string,
      opts?: {
        prefer_size?: number;
        filename?: string;
        content_type?: string;
        expires_in?: number;
      }
    ): Promise<{ url: string; expires_in: number; etag?: string }> => {
      if (!fileId) throw new Error("fileId required");
      const now = Date.now();
      const cached = fileUrlCacheRef.current.get(fileId);
      if (cached && cached.expAt - 30_000 > now) {
        return {
          url: cached.url,
          expires_in: Math.max(0, Math.floor((cached.expAt - now) / 1000)),
          etag: cached.etag,
        };
      }

      const res: any = await request(
        "files.download-url.get",
        {
          file_id: fileId,
          prefer_size: opts?.prefer_size ?? 256,
          filename: opts?.filename,
          content_type: opts?.content_type,
          expires_in: opts?.expires_in,
        },
        12_000
      );

      const data = res?.data ?? res ?? {};
      const rawUrl: string = data.url;
      const expiresInSec: number = Number(data.expires_in ?? 900);
      const etag: string | undefined = data.etag;
      const expAt = now + expiresInSec * 1000;
      const url = etag
        ? `${rawUrl}${rawUrl.includes("?") ? "&" : "?"}v=${encodeURIComponent(
            etag
          )}`
        : rawUrl;

      fileUrlCacheRef.current.set(fileId, { url, expAt, etag });
      return { url, expires_in: expiresInSec, etag };
    },
    [request]
  );

  /**********************
   * Pagination helper
   **********************/
  const listAllPages = useCallback(
    async <TItem,>(
      action: string,
      basePayload: Record<string, any>,
      extract: (data: any) => {
        items: TItem[];
        pagination: { total?: number; limit?: number; offset?: number };
      },
      timeout = CRITICAL_TIMEOUT,
      pageSize = PAGE_SIZE
    ): Promise<TItem[]> => {
      const head: any = await request(
        action,
        { ...basePayload, limit: pageSize, offset: 0 },
        timeout
      );
      const d0 = head?.data ?? head ?? {};
      const { items: firstItems, pagination: p0 } = extract(d0);
      const total = Number(p0?.total ?? firstItems.length);
      if (!total || firstItems.length >= total) return firstItems as TItem[];

      const results: TItem[] = [...firstItems];
      const offsets: number[] = [];
      for (let off = firstItems.length; off < total; off += pageSize) {
        offsets.push(off);
      }

      for (let i = 0; i < offsets.length; i += MAX_PAGE_CONCURRENCY) {
        const batch = offsets.slice(i, i + MAX_PAGE_CONCURRENCY);
        const responses = await Promise.all(
          batch.map((off) =>
            request<any>(
              action,
              { ...basePayload, limit: pageSize, offset: off },
              timeout
            )
          )
        );

        for (const r of responses) {
          const d = r?.data ?? r ?? {};
          const { items } = extract(d);
          if (Array.isArray(items) && items.length) results.push(...items);
        }
      }

      return results;
    },
    [request]
  );

  /**********************
   * Ensures
   **********************/
  const ensureUser = useCallback(async () => {
    try {
      const payload = await requestJSON(
        "users.get",
        {
          identity_id: "me",
          includePayoutMethods: "true",
          includeAddresses: "true",
          includeSettings: "true",
          includeCompany: "true",
        },
        INITIAL_LOAD_TIMEOUT
      );

      dispatch({ type: "user/set", user: normalizeUser(payload) });
      userMeta.current.loaded = true;
      await subscribeTopic(TOPIC.USER).catch(() => {});
    } catch (err: any) {
      if (
        err?.message?.includes("401") ||
        err?.message?.includes("Unauthorized") ||
        err?.message?.includes("authentication")
      ) {
        handleAuthenticationError();
        throw err;
      }
      throw err;
    }
  }, [requestJSON, subscribeTopic, handleAuthenticationError]);

  const ensureTradingAccounts = useCallback(async () => {
    if (taMeta.current.loaded) return;

    let lastErr: Error | null = null;

    for (let attempt = 0; attempt < MAX_RETRY_ATTEMPTS; attempt++) {
      try {
        if (attempt > 0) {
          await new Promise((r) =>
            setTimeout(
              r,
              RETRY_DELAYS[Math.min(attempt - 1, RETRY_DELAYS.length - 1)]
            )
          );
        }

        const items = await listAllPages(
          "users.trading-accounts.list",
          {
            identity_id: "me",
            traderDashboard: "true",
            includeStatsTotal: "true",
            includeStatsDaily: "true",
            includeBalanceHistory: "true",
            includeDrawdownHistory: "true",
          },
          (d) => ({ items: d.accounts ?? d.items ?? [], pagination: d.pagination ?? {} }),
          INITIAL_LOAD_TIMEOUT
        );

        const normalized = normalizeTradingAccounts(items);
        dispatch({ type: "tradingAccounts/upsert", list: normalized });
        taMeta.current.loaded = true;

        await subscribeTopic(TOPIC.TRADING_ACCOUNTS).catch(() => {});

        return;
      } catch (e: any) {
        lastErr = e instanceof Error ? e : new Error(String(e));
      }
    }

    if (lastErr) throw lastErr;
  }, [listAllPages, subscribeTopic]);

  const ensureFundedPayouts = useCallback(async () => {
    if (fpMeta.current.loaded) return;
    try {
      const items = await listAllPages(
        "users.funded-payouts.list",
        { identity_id: "me" },
        (d) => ({ items: d.payouts ?? d.items ?? [], pagination: d.pagination ?? {} }),
        INITIAL_LOAD_TIMEOUT
      );
      dispatch({
        type: "fundedPayouts/upsert",
        list: normalizeFundedPayouts(items),
      });
      fpMeta.current.loaded = true;
      await subscribeTopic(TOPIC.FUNDED_PAYOUTS).catch(() => {});
    } catch (err) {
      throw err;
    }
  }, [listAllPages, subscribeTopic]);

  const ensureInvoices = useCallback(async () => {
    if (invoicesMeta.current.loaded) return;
    try {
      const raw = await listAllPages(
        "users.invoices.list",
        { identity_id: "me" },
        (d) => ({ items: d.items ?? [], pagination: d.pagination ?? {} }),
        CRITICAL_TIMEOUT
      );
      const items: Invoice[] = raw.map((item: any) => normalizeInvoice(item));
      dispatch({ type: "invoices/set", list: items });
      invoicesMeta.current.loaded = true;
      await subscribeTopic(TOPIC.INVOICES).catch(() => {});
    } catch (e) {
    }
  }, [listAllPages, subscribeTopic]);

  const ensureFundedContracts = useCallback(async () => {
    if (fundedContractsMeta.current.loaded) return;
    try {
      const res: any = await request(
        "funded-contracts.list",
        { identity_id: "me" },
        CRITICAL_TIMEOUT
      );
      const raw = res?.data?.items ?? res?.data ?? [];
      const items = normalizeFundedContracts(raw);
      dispatch({ type: "fundedContracts/set", list: items });
      fundedContractsMeta.current.loaded = true;
      await subscribeTopic(TOPIC.FUNDED_CONTRACTS).catch(() => {});
    } catch (e) {
    }
  }, [request, subscribeTopic]);

  const ensureWalletBalances = useCallback(async () => {
    if (walletBalancesMeta.current.loaded) return;
    try {
      const res: any = await request(
        "wallet.balances.get",
        { identity_id: "me" },
        CRITICAL_TIMEOUT
      );
      const items: WalletSummaryBalance[] = res?.data?.items ?? [];
      dispatch({ type: "walletBalances/set", list: items });
      walletBalancesMeta.current.loaded = true;
      await subscribeTopic(TOPIC.WALLET_TRANSACTIONS).catch(() => {});
    } catch (e) {
    }
  }, [request, subscribeTopic]);

  const ensureWalletTransactions = useCallback(async () => {
    if (walletTransactionsMeta.current.loaded) return;
    try {
      const res: any = await request(
        "wallet.transactions.list",
        { identity_id: "me" },
        CRITICAL_TIMEOUT
      );
      const items: WalletTransaction[] = res?.data?.items ?? [];
      dispatch({ type: "walletTransactions/set", list: items });
      walletTransactionsMeta.current.loaded = true;
    } catch (e) {
    }
  }, [request]);

  const ensureWalletBuckets = useCallback(async () => {
    if (walletBucketsMeta.current.loaded) return;
    try {
      const res: any = await request(
        "wallet.transactions.posted-by-bucket.list",
        { identity_id: "me" },
        CRITICAL_TIMEOUT
      );
      const items = res?.data?.items ?? [];
      dispatch({ type: "walletBuckets/set", list: items });
      walletBucketsMeta.current.loaded = true;
    } catch (e) {
    }
  }, [request]);

  const reloadWalletSummaries = useCallback(async () => {
    try {
      const [balancesRes, bucketsRes] = await Promise.all([
        request("wallet.balances.get", { identity_id: "me" }, CRITICAL_TIMEOUT),
        request(
          "wallet.transactions.posted-by-bucket.list",
          { identity_id: "me" },
          CRITICAL_TIMEOUT
        ),
      ]);

      const balances: WalletSummaryBalance[] =
        (balancesRes as any)?.data?.items ?? [];
      dispatch({ type: "walletBalances/set", list: balances });

      const buckets = (bucketsRes as any)?.data?.items ?? [];
      dispatch({ type: "walletBuckets/set", list: buckets });
    } catch (e) {
    }
  }, [request]);

  const ensureAchievements = useCallback(async () => {
    if (achMeta.current.loaded) return;
    const items = await listAllPages(
      "achievements.list",
      { identity_id: "me" },
      (d) => ({ items: d.items ?? [], pagination: d.pagination ?? {} }),
      INITIAL_LOAD_TIMEOUT
    );
    const normalized = mapAchievementListToLite(items);
    dispatch({ type: "achievements/upsert", list: normalized });
    achMeta.current.loaded = true;
    await subscribeTopic(TOPIC.ACHIEVEMENTS).catch(() => {});
  }, [listAllPages, subscribeTopic]);

  const ensureTrades = useCallback(async () => {
    if (tradesMeta.current.loaded) return;
    try {
      const items = await listAllPages(
        "trades.list",
        { identity_id: "me", includeBalance: "true", includeDrawdown: "true" },
        (d) => ({ items: d.trades ?? d.items ?? [], pagination: d.pagination ?? {} }),
        INITIAL_LOAD_TIMEOUT
      );
      const normalizedItems = normalizeTradesArray(items);
      dispatch({ type: "trades/upsert", list: normalizedItems });
      tradesMeta.current.loaded = true;
      await subscribeTopic(TOPIC.TRADES).catch(() => {});
    } catch (e) {
    }
  }, [listAllPages, subscribeTopic]);

  const ensureCommissions = useCallback(async () => {
    if (commissionsMeta.current.loaded) return;
    try {
      const items = await listAllPages(
        "affiliates.commissions.list",
        {},
        (d) => ({ items: d.commissions ?? d.items ?? [], pagination: d.pagination ?? {} }),
        INITIAL_LOAD_TIMEOUT
      );
      dispatch({ type: "commissions/upsert", list: items });
      commissionsMeta.current.loaded = true;
    } catch (e) {
    }
  }, [listAllPages]);

  const ensureSettlements = useCallback(async () => {
    if (settlementsMeta.current.loaded) return;
    try {
      const response = await requestJSON(
        "affiliates.settlements.list",
        {},
        INITIAL_LOAD_TIMEOUT
      );
      const items = response.settlements ?? response.data?.settlements ?? [];
      const normalizedItems = normalizeSettlements(items);
      dispatch({ type: "settlements/upsert", list: normalizedItems });
      settlementsMeta.current.loaded = true;
    } catch (e) {
    }
  }, [requestJSON]);

  const ensurePartnerCodes = useCallback(async () => {
    if (partnerCodesMeta.current.loaded) return;
    try {
      const response = await requestJSON(
        "affiliates.codes.list",
        {},
        INITIAL_LOAD_TIMEOUT
      );
      const items = response.codes ?? [];
      dispatch({ type: "partnerCodes/upsert", list: items });
      partnerCodesMeta.current.loaded = true;
    } catch (e) {
    }
  }, [requestJSON]);

  const ensurePartnerLinks = useCallback(async () => {
    if (partnerLinksMeta.current.loaded) return;
    try {
      const response = await requestJSON(
        "affiliates.links.list",
        {},
        INITIAL_LOAD_TIMEOUT
      );
      const items = response.links ?? [];
      dispatch({ type: "partnerLinks/upsert", list: items });
      partnerLinksMeta.current.loaded = true;
    } catch (e) {
    }
  }, [requestJSON]);

  const ensurePartnerData = useCallback(async () => {
    if (partnerDataMeta.current.loaded) return;
    try {
      const response = await requestJSON(
        "affiliates.get",
        { status: "pending" },
        INITIAL_LOAD_TIMEOUT
      );
      const partnerData = response ? normalizePartnerData(response) : null;
      dispatch({ type: "partnerData/set", data: partnerData });
      partnerDataMeta.current.loaded = true;
      subscribeTopic(TOPIC.PARTNERS).catch(() => {});
    } catch (e) {
      dispatch({ type: "partnerData/set", data: null });
      partnerDataMeta.current.loaded = true;
      (partnerDataMeta.current as any).error = e instanceof Error ? e.message : String(e);
    }
  }, [requestJSON, subscribeTopic]);

  const getLevelStatesList = useCallback(
    async (payload?: { user_id?: string; scope?: "active" | "all" }) => {
      return requestJSON(
        "levels.states.list",
        { scope: "all", ...(payload || {}) },
        20_000
      );
    },
    [requestJSON]
  );

  const ensureLevelStates = useCallback(async () => {
    if (levelsMeta.current.statesLoaded) return;
    try {
      const list = await getLevelStatesList({ scope: "all" });
      const normalized = (list.items ?? []).map(normalizeLevelState);
      dispatch({ type: "levels/states/upsert", list: normalized });
      levelsMeta.current.statesLoaded = true;
      await subscribeTopic(TOPIC.LEVELS).catch(() => {});
    } catch (e) {
    }
  }, [getLevelStatesList, subscribeTopic]);

  const ensureLevelTiers = useCallback(async () => {
    if (levelTiersMeta.current.loaded) return;
    try {
      const res: any = await request(
        "levels.seasons.tiers.list",
        { season_id: "current" },
        CRITICAL_TIMEOUT
      );
      const items = (res?.data?.items ?? []).map(normalizeLevelRankTier);
      items.sort(
        (a: LevelRankTier, b: LevelRankTier) => a.sort_order - b.sort_order
      );
      dispatch({ type: "levels/tiers/set", list: items });
      levelTiersMeta.current.loaded = true;
    } catch (e) {
    }
  }, [request]);

  const refreshLevelTiers = useCallback(async () => {
    try {
      const [tiersRes, statesList] = await Promise.all([
        request(
          "levels.seasons.tiers.list",
          { season_id: "current" },
          CRITICAL_TIMEOUT
        ),
        getLevelStatesList({ scope: "all" }),
      ]);

      const tiers = ((tiersRes as any)?.data?.items ?? []).map(
        normalizeLevelRankTier
      );
      tiers.sort(
        (a: LevelRankTier, b: LevelRankTier) => a.sort_order - b.sort_order
      );
      dispatch({ type: "levels/tiers/set", list: tiers });

      const states = (statesList.items ?? []).map(normalizeLevelState);
      dispatch({ type: "levels/states/upsert", list: states });
    } catch (e) {
    }
  }, [request, getLevelStatesList]);

  const ensureTickets = useCallback(async () => {
    if (ticketsMeta.current.loaded) return;
    try {
      const items = await listAllPages(
        "tickets.list",
        {},
        (d) => ({ items: d.items ?? [], pagination: d.pagination ?? {} }),
        INITIAL_LOAD_TIMEOUT
      );
      dispatch({ type: "tickets/upsert", list: items });
      ticketsMeta.current.loaded = true;
      await subscribeTopic(TOPIC.TICKETS).catch(() => {});
    } catch (e) {
    }
  }, [listAllPages, subscribeTopic]);

  const ensureTradingStreak = useCallback(async () => {
    if (tradingStreakMeta.current.loaded) return;
    try {
      const response = await requestJSON(
        "users.trading-streaks.get",
        {},
        INITIAL_LOAD_TIMEOUT
      );
      const streakData = response ? normalizeTradingStreak(response) : null;
      dispatch({ type: "tradingStreak/set", data: streakData });
      tradingStreakMeta.current.loaded = true;
      await subscribeTopic(TOPIC.TRADING_STREAKS).catch(() => {});
    } catch (e) {
    }
  }, [requestJSON, subscribeTopic]);

  const ensureNotifications = useCallback(async () => {
    if (notificationsMeta.current.loaded) return;
    try {
      const res: any = await request(
        "notifications.list",
        { identity_id: "me" },
        CRITICAL_TIMEOUT
      );
      const items: Notification[] = normalizeNotifications(
        res?.data?.items ?? res?.items ?? []
      );
      dispatch({ type: "notifications/upsert", list: items });
      notificationsMeta.current.loaded = true;
      await subscribeTopic(TOPIC.NOTIFICATIONS).catch(() => {});
    } catch (e) {
    }
  }, [request, subscribeTopic]);

  const ensureActivityFeed = useCallback(async () => {
    if (activityFeedMeta.current.loaded) return;
    try {
      const res: any = await request(
        "users.activity-feed.list",
        { identity_id: "me" },
        CRITICAL_TIMEOUT
      );
      const items: ActivityFeedItem[] = res?.data?.items ?? [];
      dispatch({ type: "activityFeed/set", list: items });
      activityFeedMeta.current.loaded = true;
    } catch (e) {
    }
  }, [request]);

  const ensurePayoutMethodSettings = useCallback(async () => {
    if (payoutMethodSettingsMeta.current.loaded) return;
    try {
      const res: any = await request(
        "providers.payout-method-settings.get",
        {},
        CRITICAL_TIMEOUT
      );
      const items: PayoutMethodSetting[] = res?.data?.settings ?? [];
      dispatch({ type: "payoutMethodSettings/set", list: items });
      payoutMethodSettingsMeta.current.loaded = true;
    } catch (e) {
    }
  }, [request]);

  const ensureWalletPayouts = useCallback(async () => {
    if (walletPayoutsMeta.current.loaded) return;
    try {
      const res: any = await request(
        "wallet.payouts.list",
        {
          identity_id: "me",
          status: "pending|completed|rejected|canceled",
          limit: 500,
          offset: 0,
        },
        CRITICAL_TIMEOUT
      );
      const items = res?.data?.items ?? [];
      dispatch({ type: "walletPayouts/set", list: items });
      walletPayoutsMeta.current.loaded = true;
    } catch (e) {
    }
  }, [request]);

  /**********************
   * Outbox handler (server pushes)
   **********************/
  const outboxHandler = useMemo(
    () =>
      makeOutboxHandler({
        upsertUser: (u: User | null) => dispatch({ type: "user/set", user: u }),
        mergeUser: (partial: Partial<User>) =>
          dispatch({ type: "user/merge", partial }),
        upsertUserAddress: (address: Address) =>
          dispatch({ type: "user/upsertAddress", address }),
        updateUserPayoutMethods: (payout_methods: PayoutMethod[]) =>
          dispatch({ type: "user/updatePayoutMethods", payout_methods }),
        upsertUserPayoutMethod: (method: PayoutMethod) =>
          dispatch({ type: "user/upsertPayoutMethod", method }),
        removeUserPayoutMethod: (methodId: string) =>
          dispatch({ type: "user/removePayoutMethod", methodId }),
        userMeta: userMeta as MetaRef,

        upsertTradingAccounts: (list: TradingAccount[]) =>
          dispatch({ type: "tradingAccounts/upsert", list }),
        removeTradingAccounts: (ids: string[]) =>
          dispatch({ type: "tradingAccounts/remove", ids }),

        upsertFundedPayouts: (list: FundedPayout[]) =>
          dispatch({ type: "fundedPayouts/upsert", list }),
        removeFundedPayouts: (ids: string[]) =>
          dispatch({ type: "fundedPayouts/remove", ids }),

        upsertAchievements: (list: AchievementLite[]) =>
          dispatch({ type: "achievements/upsert", list }),
        removeAchievements: (ids: string[]) =>
          dispatch({ type: "achievements/remove", ids }),

        upsertTrades: (list: any[]) =>
          dispatch({ type: "trades/upsert", list }),
        removeTrades: (ids: string[]) =>
          dispatch({ type: "trades/remove", ids }),

        upsertPartnerLinks: (list: PartnerLink[]) =>
          dispatch({ type: "partnerLinks/upsert", list }),
        removePartnerLinks: (ids: string[]) =>
          dispatch({ type: "partnerLinks/remove", ids }),

        upsertPartnerCodes: (list: PartnerCode[]) =>
          dispatch({ type: "partnerCodes/upsert", list }),
        removePartnerCodes: (ids: string[]) =>
          dispatch({ type: "partnerCodes/remove", ids }),

        upsertPartnerData: (data: PartnerData | null) =>
          dispatch({ type: "partnerData/set", data }),

        upsertTickets: (list: Ticket[]) =>
          dispatch({ type: "tickets/upsert", list }),
        removeTickets: (ids: string[]) =>
          dispatch({ type: "tickets/remove", ids }),

        upsertTradingStreak: (data: TradingStreak | null) =>
          dispatch({ type: "tradingStreak/set", data }),

        upsertLevelStates: (list: LevelState[]) =>
          dispatch({ type: "levels/states/upsert", list }),
        removeLevelStates: (ids: string[]) =>
          dispatch({ type: "levels/states/remove", ids }),

        upsertWalletTransactions: (list: WalletTransaction[]) => {
          dispatch({ type: "walletTransactions/upsert", list });
          reloadWalletSummaries();
        },
        removeWalletTransactions: (ids: string[]) =>
          dispatch({ type: "walletTransactions/remove", ids }),
        upsertWalletBuckets: (list: any[]) =>
          dispatch({ type: "walletBuckets/set", list }),
        upsertWalletCategories: (_list: any[]) => {},

        upsertPositions: (_list: any[]) => {},
        removePositions: (_ids: string[]) => {},

        upsertProviders: (_list: any[]) => {},

        upsertMessages: (_list: any[]) => {},
        upsertConversations: (_list: any[]) => {},

        upsertNotifications: (list: Notification[]) =>
          dispatch({ type: "notifications/upsert", list }),
        removeNotifications: (ids: string[]) =>
          dispatch({ type: "notifications/remove", ids }),

        upsertCommissions: (list: Commission[]) =>
          dispatch({ type: "commissions/upsert", list }),
        removeCommissions: (ids: string[]) =>
          dispatch({ type: "commissions/remove", ids }),

        upsertSettlements: (list: Settlement[]) =>
          dispatch({ type: "settlements/upsert", list }),
        removeSettlements: (ids: string[]) =>
          dispatch({ type: "settlements/remove", ids }),

        upsertAgreements: (_list: any[]) => {},
        removeAgreements: (_ids: string[]) => {},

        upsertFundedContracts: (list: FundedContract[]) =>
          dispatch({ type: "fundedContracts/upsert", list }),
        upsertInvoices: (list: Invoice[]) =>
          dispatch({ type: "invoices/upsert", list }),

        upsertLevelEvents: (_list: LevelEvent[]) => {},
        removeLevelEvents: (_ids: string[]) => {},

        taMeta: taMeta as MetaRef,
        fpMeta: fpMeta as MetaRef,
        achMeta: achMeta as MetaRef,
        tradesMeta: tradesMeta as MetaRef,
        partnerLinksMeta: partnerLinksMeta as MetaRef,
        partnerCodesMeta: partnerCodesMeta as MetaRef,
        ticketsMeta: ticketsMeta as MetaRef,
        positionsMeta: { current: { loaded: false } } as MetaRef,
        providersMeta: { current: { loaded: false } } as MetaRef,
        walletMeta: walletTransactionsMeta as MetaRef,
        messagesMeta: { current: { loaded: false } } as MetaRef,
        notificationsMeta: notificationsMeta as MetaRef,
        fundedContractsMeta: fundedContractsMeta as MetaRef,
        invoicesMeta: invoicesMeta as MetaRef,
        onSessionRevoked: handleAuthenticationError,
        onNotification: () => {},
      }),
    [reloadWalletSummaries, handleAuthenticationError]
  );

  /**********************
   * Connection lifecycle (browser_id based)
   **********************/
  const safeClose = (reason: string, opts?: { keepQueue?: boolean }) => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    clientInitiatedCloseRef.current = true;

    try {
      socketRef.current?.close(1000, reason);
    } catch {}

    socketRef.current = null;

    if (hbRef.current) {
      clearInterval(hbRef.current);
      hbRef.current = null;
    }

    if (watchdogRef.current) {
      clearInterval(watchdogRef.current);
      watchdogRef.current = null;
    }

    const rejectedIds = new Set<string>();

    if (opts?.keepQueue) {
      const queuedIds = new Set(pendingQueueRef.current.map((q) => q.msg.requestId));

      for (const [id, p] of inflightRef.current.entries()) {
        clearTimeout(p.to);
        if (queuedIds.has(id)) continue;
        try {
          p.reject(new Error("Connection closed"));
          rejectedIds.add(id);
        } catch {}
      }

      inflightRef.current.clear();
      chunkBufferRef.current.clear();
      patchChunkBufferRef.current.clear();
    } else {
      for (const [id, p] of inflightRef.current.entries()) {
        try {
          p.reject(new Error("Connection closed"));
          rejectedIds.add(id);
        } catch {}
        clearTimeout(p.to);
      }

      inflightRef.current.clear();
      chunkBufferRef.current.clear();
      patchChunkBufferRef.current.clear();

      for (const item of pendingQueueRef.current) {
        if (rejectedIds.has(item.msg.requestId)) continue;
        try {
          item.reject(new Error("Connection closed"));
          rejectedIds.add(item.msg.requestId);
        } catch {}
      }
      pendingQueueRef.current = [];
    }

    setStatus("closed");
  };

  const disconnect = useCallback((opts?: { suppressReconnect?: boolean }) => {
    suppressReconnectRef.current = !!opts?.suppressReconnect;
    safeClose("disconnect-called");
  }, []);

  const connectNow = useCallback(async () => {
    const sessionOk = await ensureFreshSession();
    if (!sessionOk) {
      handleAuthenticationError();
      suppressReconnectRef.current = true;
      return;
    }

    suppressReconnectRef.current = false;

    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    if (socketRef.current) {
      safeClose("connectNow-forced");
    }

    startedKeyRef.current = null;
    setStatus("connecting");
    setReconnectTrigger((prev) => prev + 1);
  }, [handleAuthenticationError]);

  useEffect(() => {
    const onOnline = () => {
      if (!isAuthenticatedRef.current) return;
      connectNow();
    };

    const onOffline = () => {
      suppressReconnectRef.current = true;
      safeClose("offline");
    };

    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      if (!isAuthenticatedRef.current) return;

      const s = socketRef.current;
      const needsReconnect = !s || s.readyState >= WebSocket.CLOSING;
      if (!needsReconnect) return;

      (async () => {
        const ok = await ensureFreshSession();
        if (!ok) {
          handleAuthenticationError();
          return;
        }
        connectNow();
      })();
    };

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [connectNow, handleAuthenticationError]);

  useEffect(() => {
    const canConnectAuth = !isAuthLoading && isAuthenticated;

    if (!canConnectAuth) {
      disconnect({ suppressReconnect: true });
      startedKeyRef.current = null;
      dispatch({ type: "reset" });

      userMeta.current = { loaded: false };
      taMeta.current = { loaded: false };
      fpMeta.current = { loaded: false };
      invoicesMeta.current = { loaded: false };
      fundedContractsMeta.current = { loaded: false };
      walletBalancesMeta.current = { loaded: false };
      walletTransactionsMeta.current = { loaded: false };
      walletBucketsMeta.current = { loaded: false };
      achMeta.current = { loaded: false };
      tradesMeta.current = { loaded: false };
      commissionsMeta.current = { loaded: false };
      settlementsMeta.current = { loaded: false };
      partnerCodesMeta.current = { loaded: false };
      partnerLinksMeta.current = { loaded: false };
      partnerDataMeta.current = { loaded: false };
      ticketsMeta.current = { loaded: false };
      tradingStreakMeta.current = { loaded: false };
      notificationsMeta.current = { loaded: false };
      activityFeedMeta.current = { loaded: false };
      levelsMeta.current = { statesLoaded: false, eventsLoaded: false };
      levelTiersMeta.current = { loaded: false };
      payoutMethodSettingsMeta.current = { loaded: false };
      walletPayoutsMeta.current = { loaded: false };
      return;
    }

    const browserId = getBrowserIdFromCookie();
    if (!browserId) {
      disconnect({ suppressReconnect: true });
      startedKeyRef.current = null;
      return;
    }

    const identityKey = `u:auth|bid:${browserId}`;
    if (lastIdentityRef.current !== identityKey) {
      lastIdentityRef.current = identityKey;
      startedKeyRef.current = null;
      safeClose("identity-changed", { keepQueue: true });
      dispatch({ type: "reset" });

      userMeta.current = { loaded: false };
      taMeta.current = { loaded: false };
      fpMeta.current = { loaded: false };
      invoicesMeta.current = { loaded: false };
      fundedContractsMeta.current = { loaded: false };
      walletBalancesMeta.current = { loaded: false };
      walletTransactionsMeta.current = { loaded: false };
      walletBucketsMeta.current = { loaded: false };
      achMeta.current = { loaded: false };
      tradesMeta.current = { loaded: false };
      commissionsMeta.current = { loaded: false };
      settlementsMeta.current = { loaded: false };
      partnerCodesMeta.current = { loaded: false };
      partnerLinksMeta.current = { loaded: false };
      partnerDataMeta.current = { loaded: false };
      ticketsMeta.current = { loaded: false };
      tradingStreakMeta.current = { loaded: false };
      notificationsMeta.current = { loaded: false };
      activityFeedMeta.current = { loaded: false };
      levelsMeta.current = { statesLoaded: false, eventsLoaded: false };
      levelTiersMeta.current = { loaded: false };
      payoutMethodSettingsMeta.current = { loaded: false };
      walletPayoutsMeta.current = { loaded: false };
    }

    if (startedKeyRef.current === identityKey) return;
    startedKeyRef.current = identityKey;

    const connect = async () => {
      if (socketRef.current && socketRef.current.readyState < 2) {
        return;
      }

      const WS_BASE = resolveWsBase();
      const currentBid = getBrowserIdFromCookie();

      if (!currentBid) {
        disconnect({ suppressReconnect: true });
        startedKeyRef.current = null;
        return;
      }

      const sessionOk = await ensureFreshSession();
      if (!sessionOk) {
        console.log('[WS] Session invalid, aborting connect');
        handleAuthenticationError();
        suppressReconnectRef.current = true;
        startedKeyRef.current = null;
        return;
      }

      const wsUrl = `${WS_BASE}?browser_id=${encodeURIComponent(currentBid)}`;
      console.log('[WS] Connecting to', wsUrl);
      setStatus("connecting");

      const socket = new WebSocket(wsUrl, [`bid.${currentBid}`]);
      socketRef.current = socket;

      socket.onopen = async () => {
        console.log('[WS] Connection opened');
        reconnectAttemptsRef.current = 0;
        setStatus("open");

        flushPendingQueue();

        lastPongAtRef.current = Date.now();
        lastPingSentAtRef.current = 0;
        missedPongsRef.current = 0;
        connectionHealthRef.current = {
          pingSent: 0,
          pongReceived: 0,
          avgLatency: 0,
        };

        if (hbRef.current) clearInterval(hbRef.current);
        hbRef.current = window.setInterval(() => {
          const s = socketRef.current;
          if (!s || s.readyState !== WebSocket.OPEN) return;

          const now = Date.now();
          if (missedPongsRef.current >= MAX_MISSED_PONGS) {
            safeClose("missed-pongs");
            return;
          }

          const pingTime = now;
          const ok = rawSend({ type: "ping", t: pingTime });
          if (ok) {
            lastPingSentAtRef.current = pingTime;
            connectionHealthRef.current.pingSent++;
          }

          if (
            lastPingSentAtRef.current > 0 &&
            now - lastPingSentAtRef.current > HEARTBEAT_TIMEOUT
          ) {
            missedPongsRef.current++;
          }
        }, HEARTBEAT_INTERVAL);

        try {
          const results = await Promise.allSettled([
            ensureUser(),
            ensureTradingAccounts(),
            ensureFundedPayouts(),
            ensureInvoices(),
            ensureFundedContracts(),
            ensureWalletBalances(),
            ensureWalletTransactions(),
            ensureWalletBuckets(),
            ensureAchievements(),
            ensureTrades(),
            ensureCommissions(),
            ensureSettlements(),
            ensurePartnerCodes(),
            ensurePartnerLinks(),
            ensurePartnerData(),
            ensureLevelStates(),
            ensureLevelTiers(),
            ensureTickets(),
            ensureTradingStreak(),
            ensureNotifications(),
            ensureActivityFeed(),
            ensurePayoutMethodSettings(),
            ensureWalletPayouts(),
            subscribeTopic(TOPIC.MESSAGES).catch(() => {}),
          ]);

          const userResult = results[0];
          if (userResult.status === "rejected") {
            const err = userResult.reason;
            if (
              err?.message?.includes("401") ||
              err?.message?.includes("Unauthorized") ||
              err?.message?.includes("authentication")
            ) {
              handleAuthenticationError();
              suppressReconnectRef.current = true;
              safeClose("authentication-expired");
              return;
            }
          }

          const labels = [
            "ensureUser",
            "ensureTradingAccounts",
            "ensureFundedPayouts",
            "ensureInvoices",
            "ensureFundedContracts",
            "ensureWalletBalances",
            "ensureWalletTransactions",
            "ensureWalletBuckets",
            "ensureAchievements",
            "ensureTrades",
            "ensureCommissions",
            "ensureSettlements",
            "ensurePartnerCodes",
            "ensurePartnerLinks",
            "ensurePartnerData",
            "ensureLevelStates",
            "ensureLevelTiers",
            "ensureTickets",
            "ensureTradingStreak",
            "ensureNotifications",
            "ensureActivityFeed",
            "ensurePayoutMethodSettings",
            "ensureWalletPayouts",
            "sub:MESSAGES",
          ];

          const failed = results
            .map((r, i) => (r.status === "rejected" ? labels[i] : null))
            .filter(Boolean);
          if (failed.length) {
            console.error('[WS] Failed data loads:', failed);
          }
          const succeeded = results
            .map((r, i) => (r.status === "fulfilled" ? labels[i] : null))
            .filter(Boolean);
          console.log('[WS] Data load complete', { succeeded, failed });
        } catch (e) {
          console.error('[WS] Data load exception', e);
        }
      };

      socket.onmessage = (e) => {
        let payload: any = null;
        try {
          payload = JSON.parse(e.data);
        } catch {
          return;
        }

        if (payload?.type !== "pong") {
          console.log('[WS INCOMING]', payload?.action || payload?.topic || payload?.type, payload);
        }

        if (payload?.type === "pong") {
          const now = Date.now();
          const latency =
            lastPingSentAtRef.current > 0
              ? now - lastPingSentAtRef.current
              : 0;
          lastPongAtRef.current = now;
          missedPongsRef.current = 0;

          const prev = connectionHealthRef.current.avgLatency;
          const count = ++connectionHealthRef.current.pongReceived;
          connectionHealthRef.current.avgLatency =
            prev + (latency - prev) / count;
          return;
        }

        if (payload?.requestId && inflightRef.current.has(payload.requestId)) {
          const maybeAssembled = processIncomingChunk(payload);
          if (!maybeAssembled) {
            return;
          }
          payload = maybeAssembled;

          const p = inflightRef.current.get(payload.requestId)!;
          clearTimeout(p.to);
          inflightRef.current.delete(payload.requestId);
          chunkBufferRef.current.delete(payload.requestId);
          patchChunkBufferRef.current.delete(payload.requestId);

          if (payload.action === "users.update") {
            if (payload.status === "success") {
              const d = payload.data ?? payload.payload ?? {};
              const partial: Record<string, any> = {};

              if (d.display_name !== undefined) partial.display_name = d.display_name;
              if (d.community_username !== undefined)
                partial.community_username = d.community_username;
              if (d.firstname !== undefined) partial.firstname = d.firstname;
              if (d.lastname !== undefined) partial.lastname = d.lastname;
              if (d.language_code !== undefined)
                partial.language_code = d.language_code;
              if (d.timezone !== undefined) partial.timezone = d.timezone;

              if (Object.keys(partial).length > 0) {
                dispatch({ type: "user/merge", partial });
              }
            }
          }

          if (payload.action === "affiliates.update") {
            if (payload.status === "success" && payload.data) {
              const { handle, partner_display_name } = payload.data;
              if (handle && partner_display_name) {
                dispatch({
                  type: "partnerData/update",
                  handle,
                  partner_display_name,
                });
              }
            }
          }

          if (payload.action === "affiliates.get") {
            if (payload.status === "success" && payload.data) {
              const partnerData = normalizePartnerData(payload.data);
              dispatch({ type: "partnerData/set", data: partnerData });
            }
          }

          if (payload.action === "tickets.status.change") {
            if (payload.status === "success" && payload.data) {
              const updated = normalizeTicket(payload.data);
              dispatch({ type: "tickets/upsert", list: [updated] });
            }
          }

          if (payload.status === "error") {
            p.reject(new Error(payload.message || "Request failed"));
          } else {
            p.resolve(payload);
          }
          return;
        }

        if (isOutboxEnvelope(payload)) {
          outboxHandler(payload);
          return;
        }
      };

      socket.onerror = (err) => {
        console.error('[WS] Connection error', err);
        setStatus("error");
      };

      socket.onclose = (ev) => {
        console.log('[WS] Connection closed', { code: ev.code, reason: ev.reason, wasClean: ev.wasClean });
        setStatus("closed");

        if (hbRef.current) {
          clearInterval(hbRef.current);
          hbRef.current = null;
        }

        if (socketRef.current === socket) socketRef.current = null;

        for (const [, p] of inflightRef.current.entries()) {
          try {
            p.reject(new Error(`Closed (${ev.code})`));
          } catch {}
          clearTimeout(p.to);
        }
        inflightRef.current.clear();
        chunkBufferRef.current.clear();
        patchChunkBufferRef.current.clear();

        if (clientInitiatedCloseRef.current) {
          clientInitiatedCloseRef.current = false;
          if (!suppressReconnectRef.current) {
            startedKeyRef.current = null;
            setReconnectTrigger((prev) => prev + 1);
          } else {
            suppressReconnectRef.current = false;
            startedKeyRef.current = null;
          }
          return;
        }

        const isAuthError =
          ev.code === 1008 ||
          ev.code === 4001 ||
          ev.reason?.toLowerCase().includes("auth") ||
          ev.reason?.toLowerCase().includes("unauthorized");

        if (isAuthError) {
          handleAuthenticationError();
          suppressReconnectRef.current = true;
          startedKeyRef.current = null;
          return;
        }

        if (ev.code === 1000) {
          startedKeyRef.current = null;
          (async () => {
            await ensureFreshSession();
            connectNow();
          })();
          return;
        }

        if (!isAuthLoading && isAuthenticated) {
          reconnectAttemptsRef.current++;

          (async () => {
            const sessionOk = await ensureFreshSession();
            if (!sessionOk) {
              handleAuthenticationError();
              suppressReconnectRef.current = true;
              startedKeyRef.current = null;
              return;
            }

            const base = Math.min(
              RECONNECT.maxDelay,
              RECONNECT.initialDelay *
                Math.pow(RECONNECT.factor, reconnectAttemptsRef.current - 1)
            );
            const jitter = base * RECONNECT.jitter * (Math.random() - 0.5);
            const delay = Math.round(base + jitter);

            if (reconnectTimerRef.current) {
              clearTimeout(reconnectTimerRef.current);
            }

            setStatus("connecting");
            reconnectTimerRef.current = window.setTimeout(() => {
              setReconnectTrigger((prev) => prev + 1);
            }, delay);
          })();
        } else {
          startedKeyRef.current = null;
        }
      };
    };

    connectRef.current = connect;
    connect();

    if (watchdogRef.current) clearInterval(watchdogRef.current);
    watchdogRef.current = window.setInterval(() => {
      if (!navigator.onLine || !isAuthenticated) return;

      const s = socketRef.current;
      if ((!s || s.readyState >= WebSocket.CLOSING) && !reconnectTimerRef.current) {
        const since = Date.now() - lastPongAtRef.current;
        if (since > HEARTBEAT_TIMEOUT * 2) {
          startedKeyRef.current = null;
          setReconnectTrigger((prev) => prev + 1);
        }
      }
    }, WATCHDOG_INTERVAL);

    return () => {
      safeClose("effect-cleanup");
    };
  }, [isAuthLoading, isAuthenticated, disconnect, reconnectTrigger, connectNow]);

  if ((import.meta as any)?.hot) {
    (import.meta as any).hot.dispose(() => {
      safeClose("hmr-dispose");
    });
  }

  useEffect(() => {
    const onLogout = () => disconnect({ suppressReconnect: true });
    window.addEventListener("app:logout", onLogout);
    return () => window.removeEventListener("app:logout", onLogout);
  }, [disconnect]);

  const removePayoutMethod = useCallback((methodId: string) => {
    dispatch({ type: "user/removePayoutMethod", methodId });
  }, []);

  const removeTradingAccount = useCallback((accountId: string) => {
    dispatch({ type: "tradingAccounts/remove", ids: [accountId] });
  }, []);

  const updateFundedContractStatus = useCallback((contractId: string, status: string) => {
    dispatch({ type: "fundedContracts/upsert", list: [{ id: contractId, status, signed_at: new Date().toISOString() } as FundedContract] });
  }, []);

  const ctxValue = useMemo<WebSocketContextType>(
    () => ({
      status,
      sendMessage,
      request,
      publish,
      disconnect,
      connectNow,
      getLevelStatesList,
      ensureLevelStates,
      getFileDownloadUrl,
      removePayoutMethod,
      removeTradingAccount,
      updateFundedContractStatus,
      data: {
        user: data.user,
        tradingAccounts: data.tradingAccounts,
        fundedPayouts: data.fundedPayouts,
        invoices: data.invoices,
        walletBalances: data.walletBalances,
        walletTransactions: data.walletTransactions,
        walletBuckets: data.walletBuckets,
        achievements: data.achievements,
        trades: data.trades,
        commissions: data.commissions,
        settlements: data.settlements,
        partnerCodes: data.partnerCodes,
        partnerLinks: data.partnerLinks,
        partnerData: data.partnerData,
        tickets: data.tickets,
        tradingStreak: data.tradingStreak,
        notifications: data.notifications,
        fundedContracts: data.fundedContracts,
        activityFeed: data.activityFeed,
        levels: { statesBySeasonId: data.levelStatesBySeasonId },
        levelTiers: data.levelTiers,
        payoutMethodSettings: data.payoutMethodSettings,
        walletPayouts: data.walletPayouts,
      },
      meta: {
        userLoaded: userMeta.current.loaded,
        tradingAccountsLoaded: taMeta.current.loaded,
        fundedPayoutsLoaded: fpMeta.current.loaded,
        invoicesLoaded: invoicesMeta.current.loaded,
        fundedContractsLoaded: fundedContractsMeta.current.loaded,
        walletBalancesLoaded: walletBalancesMeta.current.loaded,
        walletTransactionsLoaded: walletTransactionsMeta.current.loaded,
        walletBucketsLoaded: walletBucketsMeta.current.loaded,
        achievementsLoaded: achMeta.current.loaded,
        tradesLoaded: tradesMeta.current.loaded,
        commissionsLoaded: commissionsMeta.current.loaded,
        settlementsLoaded: settlementsMeta.current.loaded,
        partnerCodesLoaded: partnerCodesMeta.current.loaded,
        partnerLinksLoaded: partnerLinksMeta.current.loaded,
        partnerDataLoaded: partnerDataMeta.current.loaded,
        partnerDataError: (partnerDataMeta.current as any).error as string | undefined,
        ticketsLoaded: ticketsMeta.current.loaded,
        tradingStreakLoaded: tradingStreakMeta.current.loaded,
        notificationsLoaded: notificationsMeta.current.loaded,
        activityFeedLoaded: activityFeedMeta.current.loaded,
        levelStatesLoaded: levelsMeta.current.statesLoaded,
        levelTiersLoaded: levelTiersMeta.current.loaded,
        payoutMethodSettingsLoaded: payoutMethodSettingsMeta.current.loaded,
        walletPayoutsLoaded: walletPayoutsMeta.current.loaded,
      },
      subscribe: subscribeTopic,
      registerOutboxHandler: (handlers: {
        upsertTickets?: (list: Ticket[]) => void;
        removeTickets?: (ids: string[]) => void;
      }) => {
        const oldUpsert = outboxHandler.upsertTickets;
        const oldRemove = outboxHandler.removeTickets;

        if (handlers.upsertTickets) {
          outboxHandler.upsertTickets = handlers.upsertTickets;
        }
        if (handlers.removeTickets) {
          outboxHandler.removeTickets = handlers.removeTickets;
        }

        return () => {
          outboxHandler.upsertTickets = oldUpsert;
          outboxHandler.removeTickets = oldRemove;
        };
      },
      ensureUser,
      ensureTradingAccounts,
      ensureFundedPayouts,
      ensureInvoices,
      ensureFundedContracts,
      ensureWalletBalances,
      ensureAchievements,
      ensureTrades,
      ensureCommissions,
      ensureSettlements,
      ensurePartnerCodes,
      ensurePartnerLinks,
      ensurePartnerData,
      ensureTickets,
      ensureTradingStreak,
      ensureNotifications,
      ensureWalletTransactions,
      ensureWalletBuckets,
      reloadWalletSummaries,
      ensureActivityFeed,
      ensureLevelTiers,
      refreshLevelTiers,
    }),
    [
      status,
      sendMessage,
      request,
      publish,
      disconnect,
      connectNow,
      getLevelStatesList,
      ensureLevelStates,
      getFileDownloadUrl,
      removePayoutMethod,
      removeTradingAccount,
      updateFundedContractStatus,
      data,
      subscribeTopic,
      outboxHandler,
      ensureUser,
      ensureTradingAccounts,
      ensureFundedPayouts,
      ensureInvoices,
      ensureFundedContracts,
      ensureWalletBalances,
      ensureAchievements,
      ensureTrades,
      ensureCommissions,
      ensureSettlements,
      ensurePartnerCodes,
      ensurePartnerLinks,
      ensurePartnerData,
      ensureTickets,
      ensureTradingStreak,
      ensureNotifications,
      ensureWalletTransactions,
      ensureWalletBuckets,
      reloadWalletSummaries,
      ensureActivityFeed,
      ensureLevelTiers,
      refreshLevelTiers,
    ]
  );

  return (
    <WebSocketContext.Provider value={ctxValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = (): WebSocketContextType => {
  const ctx = useContext(WebSocketContext);
  if (!ctx) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return ctx;
};
